import { useState, type ComponentType, type SVGProps } from 'react';
import { Mail, SmartphoneDevice } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import type { User } from '/types/user-type';
import { useDisableUserMfa, useMfaTemplate } from 'features/profile/hooks/use-mfa';
import { UserMfaType } from 'features/profile/enums/user-mfa-type-enum';
import { isMfaMethodAvailable } from 'features/profile/types/mfa.types';
import { AuthenticatorAppSetup } from 'features/profile/component/modals/authenticator-app-setup/authenticator-app-setup';
import { EmailVerification } from 'features/profile/component/modals/email-verification/email-verification';
import {
  accountBadgeClassName,
  accountOutlineButtonClassName,
  accountSectionClassName,
  accountSectionTitleClassName,
} from './account-styles';

type MfaMethod = {
  type: UserMfaType;
  titleKey: string;
  defaultTitle: string;
  descriptionKey: string;
  defaultDescription: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
};

const MFA_METHODS: MfaMethod[] = [
  {
    type: UserMfaType.EMAIL_VERIFICATION,
    titleKey: 'ACCOUNT_MFA_EMAIL',
    defaultTitle: 'Email',
    descriptionKey: 'MFA_EMAIL_DESCRIPTION',
    defaultDescription: 'Receive a verification code in your registered email to complete sign-in.',
    icon: Mail,
  },
  {
    type: UserMfaType.AUTHENTICATOR_APP,
    titleKey: 'AUTHENTICATOR_APP',
    defaultTitle: 'Authenticator App',
    descriptionKey: 'MFA_AUTHENTICATOR_DESCRIPTION',
    defaultDescription:
      'Use an authentication app or browser extension to get two-factor authentication codes when prompted.',
    icon: SmartphoneDevice,
  },
];

interface TwoFactorCardProps {
  account: User;
  isAdmin: boolean;
}

/** Same MFA rules, dialogs and APIs as the Multi-factor Authentication card in the Settings dialog. */
export function TwoFactorCard({ account, isAdmin }: TwoFactorCardProps) {
  const { t } = useTranslation();
  const { data: mfaConfig } = useMfaTemplate();
  const { mutateAsync: disableUserMfa, isPending: isDisabling } = useDisableUserMfa();
  const [setupMethod, setSetupMethod] = useState<UserMfaType | null>(null);
  const [isDisableConfirmOpen, setIsDisableConfirmOpen] = useState(false);

  const activeMethod =
    account.mfaEnabled && account.isMfaVerified ? Number(account.userMfaType) : null;
  const isOn = activeMethod !== null;

  const handleMethodClick = (method: MfaMethod) => {
    if (activeMethod === method.type) {
      setIsDisableConfirmOpen(true);
      return;
    }
    setSetupMethod(method.type);
  };

  return (
    <section className={cn(accountSectionClassName, 'flex flex-col gap-3 px-6 py-5')}>
      <div className="flex min-w-0 flex-col gap-0.5">
        <h2 className={accountSectionTitleClassName}>
          {t('ACCOUNT_TWO_FACTOR_LOGIN', { defaultValue: 'Two-factor login' })}
        </h2>
        <p className="text-sm text-grey-500 dark:text-muted-foreground">
          {t('ACCOUNT_TWO_FACTOR_LOGIN_HINT', {
            defaultValue:
              "Asks for a code from your phone when you sign in, so a stolen password isn't enough.",
          })}
        </p>
      </div>

      <ul>
        {MFA_METHODS.map((method, index) => {
          const MethodIcon = method.icon;
          const isActive = activeMethod === method.type;
          const isAvailable = isMfaMethodAvailable(mfaConfig, method.type);
          const isLast = index === MFA_METHODS.length - 1;
          const title = t(method.titleKey, { defaultValue: method.defaultTitle });

          return (
            <li
              key={method.type}
              className={cn(
                // Phones: icon, title and button share the top row and the description
                // takes the full width below. Wider screens: description sits under the title.
                'grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 py-3',
                !isLast && 'border-b border-grey-100 dark:border-border'
              )}
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-card bg-grey-100 text-grey-900 sm:row-span-2 dark:bg-muted dark:text-foreground">
                <MethodIcon width={17} height={17} strokeWidth={1.5} />
              </span>
              <div className="flex min-w-0 items-center gap-2 sm:self-end">
                <p className="text-sm font-medium leading-body text-grey-950 dark:text-foreground">
                  {title}
                </p>
                {isActive ? (
                  <span className={cn(accountBadgeClassName, 'bg-success-50 text-success-700')}>
                    {t('ACTIVE', { defaultValue: 'Active' })}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                aria-label={`${isActive ? t('DISABLE') : t('ENABLE')} ${title}`}
                disabled={!isActive && !isAvailable}
                onClick={() => handleMethodClick(method)}
                className={cn(accountOutlineButtonClassName, 'sm:row-span-2')}
              >
                {isActive ? t('DISABLE') : t('ENABLE')}
              </button>
              <p className="col-span-3 text-xs leading-heading text-grey-500 sm:col-span-1 sm:col-start-2 sm:row-start-2 sm:self-start dark:text-muted-foreground">
                {t(method.descriptionKey, { defaultValue: method.defaultDescription })}
              </p>
            </li>
          );
        })}
      </ul>

      {isAdmin && !isOn ? (
        <p className="text-sm text-grey-600 dark:text-muted-foreground">
          {t('ACCOUNT_TWO_FACTOR_ADMIN_HINT', {
            defaultValue:
              "Recommended for admins, because your account controls your team's plans and billing.",
          })}
        </p>
      ) : null}

      {setupMethod === UserMfaType.EMAIL_VERIFICATION && (
        <EmailVerification userInfo={account} onClose={() => setSetupMethod(null)} />
      )}
      {setupMethod === UserMfaType.AUTHENTICATOR_APP && (
        <AuthenticatorAppSetup userInfo={account} onClose={() => setSetupMethod(null)} />
      )}
      <ConfirmationModal
        nested
        bgBlurred
        open={isDisableConfirmOpen}
        onOpenChange={setIsDisableConfirmOpen}
        title={t('DISABLE_MFA', 'Disable MFA?')}
        description={t(
          'DISABLE_MFA_DESCRIPTION',
          "Are you sure you want to disable MFA? You'll be asked to verify yourself."
        )}
        confirmText={t('CONFIRM', 'Confirm')}
        cancelText={t('CANCEL', 'Cancel')}
        isLoading={isDisabling}
        onConfirm={async () => {
          await disableUserMfa(account.itemId);
        }}
      />
    </section>
  );
}
